import { SEO } from "@/components/SEO";
import { Helmet } from "react-helmet-async";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { NavLink } from "@/components/NavLink";
import { 
  AlertTriangle,
  Check, 
  Phone,
  Calendar,
  ArrowRight,
  RefreshCw,
  Zap,
  TrendingUp,
  X
} from "lucide-react";

const CANONICAL = "https://www.leafrushmarketing.com/lawn-care-website-redesign/";

const faqItems = [
  {
    question: "How do I know if my lawn care website needs a redesign?",
    answer: "Key signs: slow load times (over 3 seconds), not mobile-friendly, outdated design, no service pages, no quote form or click-to-call, low Google rankings, visitors leaving without contacting you. If you're getting traffic but no leads, your site likely needs work."
  },
  {
    question: "How much does a lawn care website redesign cost?",
    answer: "With our subscription model, there's no upfront redesign cost. Our Core Website plan is $200/month for a completely new site built from scratch—no fixing an old broken site, just a fresh professional build."
  },
  {
    question: "Is it better to redesign or start fresh?",
    answer: "In most cases, starting fresh is faster and more cost-effective. Patching an old site often creates more problems. We build your new site from scratch, optimized for conversions and local SEO."
  },
  {
    question: "How long does a lawn care website redesign take?",
    answer: "We typically have your new site ready for review in 3–5 business days. We build it, you review it, we refine it, and you go live when you're satisfied."
  },
  {
    question: "Will I lose my Google rankings if I redesign?",
    answer: "Properly handled, no. We set up proper redirects, maintain your URL structure where possible, and preserve the SEO signals Google already recognizes. Often, a better site structure improves rankings."
  },
  {
    question: "What if I just built my site last year?",
    answer: "Age doesn't matter—results do. If your current site isn't generating leads, a year-old site built wrong is still costing you money. The subscription model means you're not throwing away a big upfront investment."
  },
  {
    question: "Can you migrate my content to the new site?",
    answer: "Yes. We'll use your existing photos, testimonials, and service descriptions where they're good. We'll also help improve weak copy and fill gaps (like missing service pages) as part of the build."
  },
  {
    question: "What happens to my old website?",
    answer: "Once your new site is live and you're happy, we replace the old site. If you have hosting/domain access, we can point your domain to the new site. The old site essentially goes away."
  },
  {
    question: "Do I need to redesign if I'm adding new services?",
    answer: "Adding services doesn't require a full redesign—just new service pages. But if your current site can't easily accommodate new pages, or if it's outdated anyway, a redesign makes sense."
  },
  {
    question: "What's the difference between redesign and 'website refresh'?",
    answer: "A refresh usually means minor updates (new photos, updated copy). A redesign means rebuilding the site structure, layout, and conversion elements. We do full redesigns—not cosmetic patches on broken foundations."
  }
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${CANONICAL}#faq`,
  "mainEntity": faqItems.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.answer
    }
  }))
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${CANONICAL}#webpage`,
  "url": CANONICAL,
  "name": "Lawn Care Website Redesign: Fix a Site That Isn't Working",
  "description": "Your lawn care website isn't converting? Learn when to redesign, what to fix, and how we rebuild sites that actually get leads. $0 upfront, subscription model.",
  "isPartOf": {
    "@id": "https://www.leafrushmarketing.com/#website"
  },
  "about": {
    "@id": "https://www.leafrushmarketing.com/#organization"
  }
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://www.leafrushmarketing.com/#organization",
  "name": "Leaf Rush Marketing",
  "url": "https://www.leafrushmarketing.com",
  "logo": "https://www.leafrushmarketing.com/og-image.jpg"
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${CANONICAL}#service`,
  "name": "Lawn Care Website Redesign",
  "description": "Complete website redesign for lawn care businesses. We rebuild underperforming sites from scratch with conversion-focused design, local SEO, and professional structure.",
  "provider": {
    "@id": "https://www.leafrushmarketing.com/#organization"
  },
  "areaServed": {
    "@type": "Country",
    "name": "United States"
  },
  "serviceType": "Website Redesign"
};

const LawnCareWebsiteRedesign = () => {
  const warningSignals = [
    "Site loads slowly (over 3 seconds)",
    "Not mobile-friendly or hard to use on phones",
    "No dedicated service pages for each offering",
    "No quote form or click-to-call button",
    "Outdated design that looks unprofessional",
    "Getting traffic but no calls or form submissions",
    "Can't find your business on Google for local searches",
    "Competitors' sites look much better than yours"
  ];

  return (
    <>
      <SEO 
        title="Lawn Care Website Redesign: Fix a Site That Isn't Working"
        description="Your lawn care website isn't converting? Learn when to redesign, what to fix, and how we rebuild sites that actually get leads. $0 upfront."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
        ogImage="https://www.leafrushmarketing.com/og-image.jpg"
        keywords="lawn care website redesign, redesign lawn care website, improve lawn care website conversion, lawn care site redesign cost"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="pt-24">
        {/* Hero */}
        <section className="py-12 md:py-16 bg-gradient-to-b from-destructive/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <Badge variant="outline" className="mb-4 border-destructive/50 text-destructive">Problem → Solution</Badge>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Lawn Care Website Redesign: When Your Site Isn't Working
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              You have a website. It's not getting leads. Here's how to tell if you need a redesign—and how we fix it without a big upfront cost.
            </p>
          </div>
        </section>

        {/* Quick Answer Box */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="border-2 border-destructive/20">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-2 mb-6">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                  <h2 className="text-2xl font-bold">Quick Answer: Do You Need a Redesign?</h2>
                </div>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>Getting traffic but no leads?</strong> → Your site isn't converting. Redesign needed.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>Site looks outdated or unprofessional?</strong> → First impressions matter. Redesign.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>Slow, not mobile-friendly, hard to update?</strong> → Technical issues. Redesign.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                    <span><strong>Site works fine, just need minor updates?</strong> → Small changes, not a full redesign.</span>
                  </li>
                </ul>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a href="#get-started">
                    <Button size="lg" className="w-full sm:w-auto">
                      <Phone className="mr-2 h-4 w-4" />
                      Get Started
                    </Button>
                  </a>
                  <NavLink to="/lawn-care-website-design/">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      See What We Build
                    </Button>
                  </NavLink>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Warning Signs */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">8 Signs Your Lawn Care Website Needs a Redesign</h2>
            <p className="text-muted-foreground mb-8">
              If 3 or more of these apply to your current site, it's probably holding your business back:
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {warningSignals.map((signal, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-background rounded-lg border">
                  <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                  <span className="text-sm">{signal}</span>
                </div>
              ))}
            </div>

            <p className="text-sm text-muted-foreground mt-6">
              Not sure? Check our <NavLink to="/website-fixes/" className="text-primary hover:underline">website fixes hub</NavLink> for specific issues and solutions.
            </p>
          </div>
        </section>

        {/* What We Fix */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">What a Proper Redesign Fixes</h2>
            
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Zap className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Speed & Performance</h3>
                      <p className="text-muted-foreground">
                        Fast-loading pages (under 3 seconds), optimized images, mobile-first design. No more losing visitors to slow load times.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Conversion Structure</h3>
                      <p className="text-muted-foreground">
                        Clear CTAs, short quote forms, click-to-call buttons, sticky mobile contact. Make it easy for visitors to become leads.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <RefreshCw className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">SEO-Ready Structure</h3>
                      <p className="text-muted-foreground">
                        Dedicated <NavLink to="/service-pages-that-rank/" className="text-primary hover:underline">service pages</NavLink>, <NavLink to="/service-area-pages-local-seo/" className="text-primary hover:underline">service area pages</NavLink>, proper headings, schema markup. Built to rank locally.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* How We Do It */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">How We Handle Lawn Care Website Redesigns</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-background rounded-lg border">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">1</div>
                <div>
                  <h3 className="font-semibold">15-Minute Fit Call</h3>
                  <p className="text-sm text-muted-foreground">
                    We look at your current site, discuss what's not working, and confirm if a redesign is the right move.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-background rounded-lg border">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">2</div>
                <div>
                  <h3 className="font-semibold">We Build Your New Site ($0 Upfront)</h3>
                  <p className="text-sm text-muted-foreground">
                    From scratch—not patching your old site. Typically ready for review in 10–14 days.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-background rounded-lg border">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">3</div>
                <div>
                  <h3 className="font-semibold">Review & Refine</h3>
                  <p className="text-sm text-muted-foreground">
                    You review the preview, give feedback, we make adjustments until you're 100% satisfied.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-background rounded-lg border">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">4</div>
                <div>
                  <h3 className="font-semibold">Go Live</h3>
                  <p className="text-sm text-muted-foreground">
                    We replace your old site with the new one. You start your $200–$300/month subscription when it goes live.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-primary/5">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Fix Your Website?</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Book a 15-minute call. We'll review your current site, discuss what's not working, and show you how we'd rebuild it.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="#get-started">
                <Button size="lg">
                  <Calendar className="mr-2 h-4 w-4" />
                  Get Started
                </Button>
              </a>
              <NavLink to="/websites/">
                <Button size="lg" variant="outline">
                  See Pricing Details
                </Button>
              </NavLink>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl font-bold mb-6">Related Resources</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <NavLink to="/lawn-care-website-design/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Lawn Care Website Design</h3>
                <p className="text-sm text-muted-foreground">What we build and how it converts</p>
              </NavLink>
              <NavLink to="/lawn-care-website-design-pricing/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Lawn Care Website Pricing</h3>
                <p className="text-sm text-muted-foreground">Detailed pricing breakdown</p>
              </NavLink>
              <NavLink to="/website-fixes/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Website Fixes Hub</h3>
                <p className="text-sm text-muted-foreground">Common problems and how to solve them</p>
              </NavLink>
              <NavLink to="/resources/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Free Resources</h3>
                <p className="text-sm text-muted-foreground">Guides and checklists for lawn care businesses</p>
              </NavLink>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-8">Lawn Care Website Redesign FAQ</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Calendly */}
        <section id="schedule-call" className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Book Your 15-Minute Fit Call</h2>
              <p className="text-muted-foreground">
                We'll review your current site, discuss what's not working, and show you how we'd rebuild it.
              </p>
            </div>
            <CalendlyEmbed />
          </div>
        </section>
      </div>
    </>
  );
};

export default LawnCareWebsiteRedesign;
